/**
 * Admin Payout Ops - P4 Feature
 * 
 * Ledger-only payout management for LoveLink referral commissions.
 * - KPI tiles (On Hold, Eligible, Paid)
 * - Tabs: Eligible, On Hold, Paid
 * - Bulk "Mark Paid" with method/reference
 * - CSV Export
 */

import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { ADMIN_ROUTES } from "../../config/adminRoutes";
import { 
  ArrowLeft, RefreshCw, DollarSign, Clock, CheckCircle, 
  Download, Users, AlertTriangle, ExternalLink
} from "lucide-react";

const API = window.location.origin;

// Format cents to dollars
function fmt$(cents) {
  if (typeof cents !== "number") return "$0.00";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return "—";
  }
}

// KPI Tile
function KPITile({ label, value, subValue, icon: Icon, color = "slate" }) {
  const colorClasses = {
    slate: "border-slate-700 bg-slate-800/50",
    emerald: "border-emerald-500/30 bg-emerald-500/10",
    amber: "border-amber-500/30 bg-amber-500/10",
    purple: "border-purple-500/30 bg-purple-500/10",
  };
  const iconColors = {
    slate: "text-slate-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    purple: "text-purple-400",
  };

  return (
    <div className={`rounded-2xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500 mb-1">{label}</div>
          <div className="text-2xl font-bold text-slate-100">{value}</div>
          {subValue && <div className="text-xs text-slate-400 mt-1">{subValue}</div>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  );
}

// Status badge
function StatusBadge({ status }) {
  const styles = {
    eligible: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    on_hold: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    paid: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full border ${styles[status] || styles.on_hold}`}>
      {status?.replace("_", " ")}
    </span>
  );
}

export default function AdminPayoutsPage() {
  const { token } = useContext(AuthContext);
  const nav = useNavigate();
  
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState("eligible");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Mark paid modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payMethod, setPayMethod] = useState("cashapp");
  const [payRef, setPayRef] = useState("");
  const [payNote, setPayNote] = useState("");

  async function loadSummary() {
    try {
      const res = await fetch(`${API}/api/admin/payouts/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load summary");
      setSummary(await res.json());
    } catch (e) {
      console.error(e);
    }
  }

  async function loadQueue(status = activeTab) {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/payouts/queue?status=${status}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load queue");
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadSummary();
    loadQueue();
    
  }, []);

  useEffect(() => {
    loadQueue(activeTab);
    
  }, [activeTab]);

  function toggleSelect(id) {
    const newSet = new Set(selected);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelected(newSet);
  }

  function selectAll() {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.id)));
    }
  }

  async function handleMarkPaid() {
    if (selected.size === 0) return;
    
    setBusy(true);
    setToast(null);
    try {
      const body = {
        items: Array.from(selected).map(id => ({
          id,
          paid_ref: payRef,
          note: payNote,
        })),
        paid_method: payMethod,
      };
      
      const res = await fetch(`${API}/api/admin/payouts/mark-paid`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to mark paid");
      
      setToast({
        type: "ok",
        msg: `Marked ${data.summary?.success_count || 0} items as paid (${fmt$(data.summary?.total_paid_cents || 0)})`
      });
      
      setShowPayModal(false);
      setPayRef("");
      setPayNote("");
      setSelected(new Set());
      
      // Reload data
      await loadSummary();
      await loadQueue();
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function handleExport() {
    window.open(`${API}/api/admin/payouts/export?status=${activeTab}`, "_blank");
  }

  const tabs = [
    { key: "eligible", label: "Eligible", count: summary?.eligible_count },
    { key: "on_hold", label: "On Hold", count: summary?.on_hold_count },
    { key: "paid", label: "Paid", count: summary?.paid_30d_count },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => nav(ADMIN_ROUTES.DASHBOARD)}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </button>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Payout Ops
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              LoveLink referral commissions • {summary?.hold_days || 30}-day hold policy
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-800 transition"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => { loadSummary(); loadQueue(); }}
              disabled={busy}
              className="flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
            toast.type === "ok" 
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" 
              : "border-rose-500/30 bg-rose-500/10 text-rose-400"
          }`}>
            {toast.msg}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KPITile
            label="Eligible to Pay"
            value={fmt$(summary?.eligible_amount)}
            subValue={`${summary?.eligible_count || 0} payees`}
            icon={CheckCircle}
            color="emerald"
          />
          <KPITile
            label="On Hold"
            value={fmt$(summary?.on_hold_amount)}
            subValue={`${summary?.on_hold_count || 0} pending`}
            icon={Clock}
            color="amber"
          />
          <KPITile
            label="Paid (30d)"
            value={fmt$(summary?.paid_30d_amount)}
            subValue={`${summary?.paid_30d_count || 0} payouts`}
            icon={DollarSign}
            color="purple"
          />
          <KPITile
            label="Total Pending"
            value={fmt$(summary?.total_pending_amount)}
            subValue={`${summary?.total_pending_count || 0} total`}
            icon={Users}
            color="slate"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                activeTab === tab.key
                  ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                  : "bg-slate-800/50 text-slate-400 border border-slate-700 hover:bg-slate-800"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-2 text-xs opacity-60">({tab.count})</span>
              )}
            </button>
          ))}
        </div>

        {/* Bulk Actions */}
        {activeTab === "eligible" && selected.size > 0 && (
          <div className="mb-4 p-3 rounded-xl border border-purple-500/30 bg-purple-500/10 flex items-center justify-between">
            <span className="text-sm text-purple-400">
              {selected.size} selected ({fmt$(items.filter(i => selected.has(i.id)).reduce((sum, i) => sum + (i.commission_amount_cents || 0), 0))})
            </span>
            <button
              onClick={() => setShowPayModal(true)}
              className="px-4 py-2 rounded-xl bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 transition"
            >
              Mark as Paid
            </button>
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-800/50 text-xs text-slate-500 font-medium">
            {activeTab === "eligible" && (
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={selected.size === items.length && items.length > 0}
                  onChange={selectAll}
                  className="rounded"
                />
              </div>
            )}
            <div className={activeTab === "eligible" ? "col-span-2" : "col-span-3"}>Date</div>
            <div className="col-span-3">Payee</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-2">Product</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-1">Status</div>
          </div>

          {/* Rows */}
          <div className="max-h-[500px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500">No items in this category</div>
            ) : (
              items.map(item => (
                <div 
                  key={item.id} 
                  className="grid grid-cols-12 gap-4 px-4 py-3 border-t border-slate-800 hover:bg-slate-800/30 transition"
                >
                  {activeTab === "eligible" && (
                    <div className="col-span-1">
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        className="rounded"
                      />
                    </div>
                  )}
                  <div className={activeTab === "eligible" ? "col-span-2" : "col-span-3"}>
                    <div className="text-sm text-slate-300">{fmtDate(item.created_at)}</div>
                    {item.effective_status === "on_hold" && (
                      <div className="text-xs text-amber-400">Clears {fmtDate(item.hold_clears_on)}</div>
                    )}
                    {item.effective_status === "paid" && (
                      <div className="text-xs text-purple-400">Paid {fmtDate(item.paid_at)}</div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <button
                      onClick={() => nav(ADMIN_ROUTES.USER_360(item.inviter_id))}
                      className="text-sm text-slate-300 hover:text-purple-400 transition truncate block"
                    >
                      {item.inviter_email || item.inviter_id?.slice(0, 8)}
                    </button>
                  </div>
                  <div className="col-span-2">
                    <div className="text-sm text-slate-400 truncate">{item.invitee_email || item.invitee_id?.slice(0, 8)}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-sm text-slate-400 truncate">{item.product_label}</div>
                  </div>
                  <div className="col-span-1 text-right">
                    <div className="text-sm font-medium text-emerald-400">{fmt$(item.commission_amount_cents)}</div>
                  </div>
                  <div className="col-span-1">
                    <StatusBadge status={item.effective_status} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-xs text-slate-500 text-center">
          Showing {items.length} of {total} items • Last updated: {summary?.as_of ? new Date(summary.as_of).toLocaleString() : '—'}
        </div>
      </div>

      {/* Mark Paid Modal */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Mark as Paid</h3>
            <p className="text-sm text-slate-400 mb-4">
              Marking {selected.size} items as paid ({fmt$(items.filter(i => selected.has(i.id)).reduce((sum, i) => sum + (i.commission_amount_cents || 0), 0))})
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Payment Method</label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm"
                >
                  <option value="cashapp">Cash App</option>
                  <option value="venmo">Venmo</option>
                  <option value="paypal">PayPal</option>
                  <option value="stripe">Stripe</option>
                  <option value="manual">Manual/Other</option>
                </select>
              </div>
              
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Reference (optional)</label>
                <input
                  type="text"
                  value={payRef}
                  onChange={(e) => setPayRef(e.target.value)}
                  placeholder="e.g. $cashtag, confirmation #"
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm"
                />
              </div>
              
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Note (optional)</label>
                <textarea
                  value={payNote}
                  onChange={(e) => setPayNote(e.target.value)}
                  placeholder="Any additional notes..."
                  rows={2}
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm resize-none"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPayModal(false)}
                className="flex-1 px-4 py-2 rounded-xl border border-slate-700 text-sm font-medium hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkPaid}
                disabled={busy}
                className="flex-1 px-4 py-2 rounded-xl bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 disabled:opacity-50 transition"
              >
                {busy ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
